/**
 * `/catalog` — Product Catalog + mapping editor (Shopify → DTF, catalog-mapping v1).
 *
 * Sync a store's real Shopify products, then map each product to an S&S blank style + a set of
 * print placements. Every variant's blank is resolved on save; youth variants resolve against a
 * SEPARATE youth blank style. Browse is any signed-in user; Sync is admin-only (server-enforced).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Boxes, Loader2, Plus, RefreshCw, Trash2, X, ZoomIn } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { PAGE_CONTAINER_CLASS } from '@/components/page-container';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchAllDesigns } from '@/lib/api/designs';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { CapsEditorBody } from './caps-mapping-editor';
import {
  bareBlankSku,
  blankIdentityLabel,
  buildColorwayPlans,
  buildMappingSaveRequest,
  CATALOG_BLANK_RESOLVE_META,
  CATALOG_MAPPING_STATE_META,
  type CapCatalogProduct,
  type CatalogProductSummary,
  type CatalogVariantSummary,
  type ColorwayPlan,
  colorwaysSharingOneBlank,
  distinctColorways,
  draftFromPlacement,
  draftMethod,
  emptyPlacementDraft,
  hasYouthVariants,
  MAX_PLACEMENTS,
  PLACEMENT_LOCATION_OPTIONS,
  type PlacementDraft,
  rankStylePairs,
  summarizeCatalog,
  usesFallbackImage,
  type VariantResolveResult,
  withImageWidth,
} from './catalog-lib';
import { DesignCombobox } from './design-combobox';
import type { DesignSummary } from './design-lib';
import { ColorCombobox } from './screen-print-quote/color-combobox';
import { StyleCombobox } from './screen-print-quote/style-combobox';

type CatalogFilter = 'all' | 'unmapped' | 'mapped' | 'caps' | 'non-apparel';

/** Mirror of the server's SYNC_STALE_MS (rest-api catalog route): a non-terminal job older than this
 *  is a wedged sync, not live work — so the button re-enables and polling stops. */
const SYNC_STALE_MS = 15 * 60 * 1000;

/** A sync job is "active" (poll it, keep the button busy) only while QUEUED/RUNNING AND fresh. */
function jobIsActive(job: { status: string; updatedAt: string } | null | undefined): boolean {
  if (!job || (job.status !== 'QUEUED' && job.status !== 'RUNNING')) return false;
  return Date.now() - Date.parse(job.updatedAt) < SYNC_STALE_MS;
}

export function CatalogPage() {
  const client = useApiClient();
  const { state: authState } = useAuth();
  // Catalog sync is an `admin`-group action (server 403s POST /catalog/:id/sync). Reflect that in the
  // button so a non-admin isn't offered an action that fails — the server gate stays authoritative.
  const canSync = authState.status === 'signed-in' && authState.role.isAdmin;
  const [storeId, setStoreId] = useState('');
  // ?filter=caps gives the caps MAPPING view a real, linkable address (P3, F5-8) — the /caps
  // worklist links here instead of hiding the mapping behind an unlabeled select. ?filter=non-apparel
  // does the same for the Ship-only classification view.
  const urlFilter = (useSearch({ strict: false }) as { filter?: string }).filter;
  const [filter, setFilter] = useState<CatalogFilter>(
    urlFilter === 'caps' ||
      urlFilter === 'mapped' ||
      urlFilter === 'unmapped' ||
      urlFilter === 'non-apparel'
      ? urlFilter
      : 'all',
  );
  const [editing, setEditing] = useState<CatalogProductSummary | null>(null);

  const storesQuery = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const { data, error } = await client.GET('/stores');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load stores.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data.items;
    },
  });
  const stores = storesQuery.data ?? [];
  useEffect(() => {
    if (!storeId && stores.length > 0) setStoreId(stores[0]?.storeId ?? '');
  }, [storeId, stores]);

  // The catalog can be large (100s of products), and the browse endpoint is cursor-paginated
  // (100/page). Load ALL pages so the grid shows every product and the mapped/unmapped counts are
  // accurate — a store this size is a few sequential fetches, not worth a manual pager.
  // The apparel browse (all/mapped/unmapped) vs the two scan-backed side lists (caps, non-apparel).
  const isApparelBrowse = filter !== 'caps' && filter !== 'non-apparel';
  const productsQuery = useInfiniteQuery({
    queryKey: ['catalog', storeId, filter],
    // The browse endpoint is apparel-only (all/mapped/unmapped); caps + non-apparel use separate scans.
    enabled: storeId.length > 0 && isApparelBrowse,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await client.GET('/catalog/{storeId}/products', {
        // `filter` is one of the browse enum's values here (the query is disabled otherwise); the
        // inline comparisons only narrow the type for the side-list filters.
        params: {
          path: { storeId },
          query: {
            filter: filter === 'caps' || filter === 'non-apparel' ? 'all' : filter,
            limit: 100,
            cursor: pageParam,
          },
        },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load products.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Caps (NON_DTF) products — a separate scan-backed list (they're not in the apparel browse GSI).
  const capsQuery = useQuery({
    queryKey: ['catalog-caps', storeId],
    enabled: storeId.length > 0 && filter === 'caps',
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/{storeId}/caps', {
        params: { path: { storeId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load caps.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data.items;
    },
  });

  // Non-apparel products (decals / stickers / pre-made goods) — also outside the apparel browse GSI.
  // This list exists so the operator can reach "Produced by FSP?" for them: it's where Ship-only
  // candidates get classified.
  const nonApparelQuery = useQuery({
    queryKey: ['catalog-nonapparel', storeId],
    enabled: storeId.length > 0 && filter === 'non-apparel',
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/{storeId}/non-apparel', {
        params: { path: { storeId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load non-apparel products.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data.items;
    },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = productsQuery;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const qc = useQueryClient();
  // The sync runs in a background worker now: POST enqueues, then we poll the job for completion.
  const sync = useMutation({
    mutationFn: async () => {
      const res = await client.POST('/catalog/{storeId}/sync', {
        params: { path: { storeId } },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Sync failed.');
      if (!res.data?.ok) throw new Error('Empty response.');
      return res.data.data.job;
    },
    // Kick the poller so the button reflects QUEUED/RUNNING immediately.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog-sync', storeId] }),
  });

  // Poll the store's latest sync job; back off to no polling once it's terminal, absent, OR stale.
  const syncStatusQuery = useQuery({
    queryKey: ['catalog-sync', storeId],
    enabled: !!storeId,
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/{storeId}/sync', {
        params: { path: { storeId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load sync status.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data.job;
    },
    refetchInterval: (query) => (jobIsActive(query.state.data ?? null) ? 2500 : false),
  });
  const syncJob = syncStatusQuery.data ?? null;
  // A QUEUED/RUNNING job that has gone stale is a wedged sync (worker hard-killed / enqueue failed);
  // don't treat it as active — re-enable the button so a re-click supersedes it (matches the server's
  // SYNC_STALE_MS dedupe). A fresh POST is also always active while the mutation is pending.
  const syncActive = sync.isPending || jobIsActive(syncJob);
  const syncStale =
    !!syncJob &&
    (syncJob.status === 'QUEUED' || syncJob.status === 'RUNNING') &&
    !jobIsActive(syncJob);

  // Refresh the product browse when a sync FINISHES this session — i.e. a QUEUED/RUNNING job we were
  // watching flips to SUCCEEDED. Guarding on the transition (not just "status === SUCCEEDED") avoids
  // re-fetching the whole catalog on mount/store-switch just because the last sync succeeded earlier.
  const prevSyncStatus = useRef<string | null>(null);
  const prevStore = useRef<string>('');
  useEffect(() => {
    const status = syncJob?.status ?? null;
    if (prevStore.current !== storeId) {
      // Store changed — re-baseline the tracker; a carried-over/pre-existing status is not a transition.
      prevStore.current = storeId;
      prevSyncStatus.current = status;
      return;
    }
    if (
      prevSyncStatus.current &&
      prevSyncStatus.current !== 'SUCCEEDED' &&
      status === 'SUCCEEDED'
    ) {
      qc.invalidateQueries({ queryKey: ['catalog', storeId] });
    }
    prevSyncStatus.current = status;
  }, [syncJob?.status, storeId, qc]);

  const products = (productsQuery.data?.pages.flatMap((p) => p.items) ??
    []) as CatalogProductSummary[];
  const counts = summarizeCatalog(products);

  return (
    <div className={cn(PAGE_CONTAINER_CLASS, 'space-y-6')}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Product catalog</h1>
          <p className="text-sm text-muted-foreground">
            Map each Shopify product to an S&amp;S blank + its print placements. Youth sizes use a
            separate youth blank style.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stores.length > 0 && (
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger className="w-40" aria-label="Store">
                <SelectValue placeholder="Store" />
              </SelectTrigger>
              <SelectContent>
                {stores.map((s) => (
                  <SelectItem key={s.storeId} value={s.storeId}>
                    {s.brandLabel ?? s.storeId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={filter} onValueChange={(v) => setFilter(v as CatalogFilter)}>
            <SelectTrigger className="w-36" aria-label="Filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unmapped">Needs mapping</SelectItem>
              <SelectItem value="mapped">Mapped</SelectItem>
              <SelectItem value="caps">Caps</SelectItem>
              <SelectItem value="non-apparel">Non-apparel</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={!storeId || syncActive || !canSync}
            onClick={() => sync.mutate()}
            title={canSync ? undefined : 'Only an admin can sync the catalog'}
          >
            {syncActive ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {syncActive ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
        </div>
      </header>

      {stores.length === 0 && storesQuery.isSuccess && (
        <Alert>
          <AlertTitle>No stores connected</AlertTitle>
          <AlertDescription>
            {/* Pre-existing instance of the grid-row trap above: the Link, the space and the trailing
                text were three children of a `grid gap-1`, so they rendered on three lines. */}
            <p>
              <Link to="/settings/stores" className="font-medium underline underline-offset-4">
                Register a store
              </Link>{' '}
              first, then sync its catalog here.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {sync.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t start the sync</AlertTitle>
          <AlertDescription>
            {sync.error instanceof Error ? sync.error.message : 'Sync failed to enqueue.'}
          </AlertDescription>
        </Alert>
      )}
      {syncActive && (
        <p className="text-sm text-muted-foreground">
          Syncing this store’s catalog in the background… this can take a minute for a large store.
        </p>
      )}
      {syncStale && (
        <Alert variant="destructive">
          <AlertTitle>Last sync didn’t finish</AlertTitle>
          <AlertDescription>
            The previous sync stopped without completing. Click “Sync from Shopify” to run it again.
          </AlertDescription>
        </Alert>
      )}
      {syncJob?.status === 'FAILED' && (
        <Alert variant="destructive">
          <AlertTitle>Last sync failed</AlertTitle>
          <AlertDescription>
            {syncJob.error ?? 'The sync worker reported an error.'}
          </AlertDescription>
        </Alert>
      )}
      {syncJob?.status === 'SUCCEEDED' && syncJob.summary && (
        <p className="text-sm text-muted-foreground">
          Synced {syncJob.summary.apparelKept} apparel product
          {syncJob.summary.apparelKept === 1 ? '' : 's'} ({syncJob.summary.nonApparel} non-apparel
          skipped) across {syncJob.summary.pages} page{syncJob.summary.pages === 1 ? '' : 's'}.
        </p>
      )}

      {isApparelBrowse && (
        <>
          {productsQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn't load products</AlertTitle>
              <AlertDescription>
                {productsQuery.error instanceof Error
                  ? productsQuery.error.message
                  : 'Network error.'}
              </AlertDescription>
            </Alert>
          )}

          {productsQuery.isPending && storeId && <Skeleton className="h-64 w-full" />}

          {productsQuery.isSuccess &&
            (products.length === 0 ? (
              <EmptyState filter={filter} />
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {counts.mapped} mapped · {counts.partial} partial · {counts.unmapped} unmapped ·{' '}
                  {counts.total} total
                  {productsQuery.isFetchingNextPage && (
                    <span className="ml-2 inline-flex items-center gap-1">
                      <Loader2 className="size-3 animate-spin" /> loading more…
                    </span>
                  )}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {products.map((p) => (
                    <ProductCard key={p.productId} product={p} onEdit={() => setEditing(p)} />
                  ))}
                </div>
              </>
            ))}
        </>
      )}

      {filter === 'caps' && (
        <>
          {capsQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn't load caps</AlertTitle>
              <AlertDescription>
                {capsQuery.error instanceof Error ? capsQuery.error.message : 'Network error.'}
              </AlertDescription>
            </Alert>
          )}
          {capsQuery.isPending && storeId && <Skeleton className="h-64 w-full" />}
          {capsQuery.isSuccess &&
            (capsQuery.data.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
                <Boxes className="size-8 text-muted-foreground" />
                <p className="text-sm font-medium">No cap products</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  A product shows here once it syncs as a cap (hat / snapback / trucker). Sync from
                  Shopify if you just added one.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {capsQuery.data.length} cap{capsQuery.data.length === 1 ? '' : 's'} ·{' '}
                  {capsQuery.data.filter((c) => !!c.capBlankSsSku).length} mapped to a blank ·{' '}
                  {capsQuery.data.filter((c) => !c.capBlankSsSku).length} need a blank
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {capsQuery.data.map((c) => (
                    <CapCard key={c.productId} cap={c} onEdit={() => setEditing(c)} />
                  ))}
                </div>
              </>
            ))}
        </>
      )}

      {filter === 'non-apparel' && (
        <>
          {nonApparelQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn't load non-apparel products</AlertTitle>
              <AlertDescription>
                {nonApparelQuery.error instanceof Error
                  ? nonApparelQuery.error.message
                  : 'Network error.'}
              </AlertDescription>
            </Alert>
          )}
          {nonApparelQuery.isPending && storeId && <Skeleton className="h-64 w-full" />}
          {nonApparelQuery.isSuccess &&
            (nonApparelQuery.data.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
                <Boxes className="size-8 text-muted-foreground" />
                <p className="text-sm font-medium">No non-apparel products</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  Everything this store synced is a tee, hoodie, or cap. Products like decals and
                  stickers show here after a sync.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {nonApparelQuery.data.length} non-apparel product
                  {nonApparelQuery.data.length === 1 ? '' : 's'} ·{' '}
                  {
                    nonApparelQuery.data.filter((p) => p.productionClass === 'FULFILLMENT_ONLY')
                      .length
                  }{' '}
                  ship-only. Open one and set “Produced by FSP?” to put it on the Ship-only
                  worklist.
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {nonApparelQuery.data.map((p) => (
                    <NonApparelCard key={p.productId} product={p} onEdit={() => setEditing(p)} />
                  ))}
                </div>
              </>
            ))}
        </>
      )}

      {editing && (
        // key per product → a fresh editor (and fresh form state) whenever the target changes.
        <MappingEditor
          key={editing.productId}
          storeId={storeId}
          product={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/**
 * The product's Shopify featured image — a CDN mockup, reference only, never printed — with a
 * Boxes-icon fallback when a product has no image. Shared by the catalog grid card (`sm`) and the
 * mapping-modal header (`lg`) so both render identically from one source.
 */
export function ProductThumb({ url, size = 'sm' }: { url?: string | null; size?: 'sm' | 'lg' }) {
  const box = size === 'lg' ? 'size-20' : 'size-14';
  const icon = size === 'lg' ? 'size-8' : 'size-6';
  // px = the box in CSS pixels (size-20 = 80, size-14 = 56); request 2× for retina and let the
  // Shopify CDN downscale, plus native lazy-load + fixed dimensions so off-screen grid rows don't
  // eagerly download and layout is reserved before the image lands.
  const boxPx = size === 'lg' ? 80 : 56;
  return url ? (
    <img
      src={withImageWidth(url, boxPx * 2)}
      alt=""
      width={boxPx}
      height={boxPx}
      loading="lazy"
      decoding="async"
      className={`${box} shrink-0 rounded-md border object-cover`}
    />
  ) : (
    <div className={`${box} flex shrink-0 items-center justify-center rounded-md border bg-muted`}>
      <Boxes className={`${icon} text-muted-foreground`} />
    </div>
  );
}

/**
 * Full-size view of a product's Shopify image — the reason the thumbnail is in the modal: staff need
 * to see the mockup large enough to confirm they're mapping the right blank. Opened from the modal
 * header thumbnail; a plain Dialog (contained image, Escape / backdrop / close to dismiss).
 */
export function ProductImageLightbox({
  url,
  title,
  onClose,
  triggerRef,
}: {
  url: string;
  title: string;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl"
        onCloseAutoFocus={(e) => {
          // Radix restores focus to a <DialogTrigger>, but this lightbox is opened via state (no
          // trigger), so its default would drop focus to <body>. Return it to the thumbnail button.
          if (triggerRef?.current) {
            e.preventDefault();
            triggerRef.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Shopify product image — reference only, never printed.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center overflow-hidden rounded-lg border bg-muted">
          <img src={url} alt={title} className="max-h-[70vh] w-auto max-w-full object-contain" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ product, onEdit }: { product: CatalogProductSummary; onEdit: () => void }) {
  const state = CATALOG_MAPPING_STATE_META[product.mappingState];
  return (
    <button
      type="button"
      onClick={onEdit}
      className="flex flex-col gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start gap-3">
        <ProductThumb url={product.featuredImageUrl} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{product.title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {product.variantCount} variant{product.variantCount === 1 ? '' : 's'}
            {product.apparelKind ? ` · ${product.apparelKind}` : ''}
          </div>
        </div>
      </div>
      <Badge variant="outline" className={`w-fit ${state.className}`}>
        {state.label}
      </Badge>
    </button>
  );
}

/** Catalog card for a NON_DTF cap — shows its current S&S blank (or "needs a blank"). Opens the caps
 *  editor (via the shared MappingEditor, which branches on productionClass). */
function CapCard({ cap, onEdit }: { cap: CapCatalogProduct; onEdit: () => void }) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className="flex flex-col gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start gap-3">
        <ProductThumb url={cap.featuredImageUrl} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{cap.title}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">Cap · off the gang sheet</div>
        </div>
      </div>
      {cap.capBlankSsSku ? (
        <Badge
          variant="outline"
          className="w-fit whitespace-normal text-left border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
        >
          {blankIdentityLabel({
            brandName: cap.capBlankBrandName,
            styleName: cap.capBlankStyleName,
            colorName: cap.capBlankColorName,
            sizeName: cap.capBlankSizeName,
          }) ?? (
            <>
              Blank <span className="ml-1 font-mono">{cap.capBlankSsSku}</span>
            </>
          )}
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className="w-fit border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
        >
          Needs a blank
        </Badge>
      )}
    </button>
  );
}

/** How each effective production class reads on a non-apparel card. Ship-only is the state this view
 *  exists to set (green); an unrouted class (no override yet, or the heuristic's NOT_PRODUCED guess)
 *  reads as still needing the operator's call. */
const NON_APPAREL_CLASS_BADGE: Record<ProductionClassValue, { label: string; className: string }> =
  {
    FULFILLMENT_ONLY: {
      label: 'Ship-only',
      className:
        'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
    },
    NOT_PRODUCED: { label: 'Not handled by FSP', className: 'text-muted-foreground' },
    NON_DTF: { label: 'Produced — off-sheet', className: '' },
    DTF_APPAREL: { label: 'Produced — DTF', className: '' },
    UNCLASSIFIED: {
      label: 'Needs classification',
      className:
        'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
    },
  };

/** Catalog card for a non-apparel product (decal / sticker / pre-made — never on the apparel browse
 *  list). Shows the EFFECTIVE production class; opens the shared MappingEditor, whose "Produced by
 *  FSP?" control is the whole point of this view. Exported for unit tests. */
export function NonApparelCard({
  product,
  onEdit,
}: {
  product: CatalogProductSummary;
  onEdit: () => void;
}) {
  const badge = NON_APPAREL_CLASS_BADGE[product.productionClass];
  return (
    <button
      type="button"
      onClick={onEdit}
      className="flex flex-col gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start gap-3">
        <ProductThumb url={product.featuredImageUrl} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{product.title}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {product.productType || product.category || 'Non-apparel'}
            {` · ${product.variantCount} variant${product.variantCount === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>
      <Badge variant="outline" className={`w-fit ${badge.className}`}>
        {badge.label}
      </Badge>
    </button>
  );
}

function EmptyState({ filter }: { filter: CatalogFilter }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
      <Boxes className="size-8 text-muted-foreground" />
      <p className="text-sm font-medium">
        {filter === 'mapped' ? 'No mapped products yet' : 'No products'}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        Click “Sync from Shopify” to pull this store's T-shirts and hoodies into the catalog.
      </p>
    </div>
  );
}

function MappingEditor({
  storeId,
  product,
  onClose,
}: {
  storeId: string;
  product: CatalogProductSummary;
  onClose: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbRef = useRef<HTMLButtonElement>(null);

  const detailQuery = useQuery({
    queryKey: ['catalog-detail', storeId, product.productId],
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/{storeId}/products/{productId}', {
        params: { path: { storeId, productId: product.productId } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load product.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data;
    },
  });

  // The EFFECTIVE class decides which editor body renders. Prefer the DETAIL's value — the server
  // folds the operator override into it (review fix, P2) — so saving "Ship-only" flips this editor on
  // the refetch; the list row's summary is suggestion-derived and would contradict the control forever.
  const effectiveClass = detailQuery.data?.product.productionClass ?? product.productionClass;
  // A NON_DTF cap maps to ONE S&S blank (no placements) via the caps editor; DTF apparel uses the
  // placement mapping editor; a FULFILLMENT_ONLY (Ship-only) product needs neither — it's packed from
  // shelf stock (P2b adds its stock-item mapping).
  const isCap = effectiveClass === 'NON_DTF';
  const isShipOnly = effectiveClass === 'FULFILLMENT_ONLY';
  // A non-apparel product (decal / sticker / pre-made — apparelKind null, no synced variants) that
  // hasn't been explicitly rescued onto the DTF path has nothing to map: no blank, no placements, no
  // designs. Show a classify-me panel instead of the (meaningless) mapping editor. A DTF_APPAREL
  // override still gets the mapping editor — that's the misread-rescue path.
  const isUnproducible =
    product.apparelKind == null && !isCap && !isShipOnly && effectiveClass !== 'DTF_APPAREL';

  const designsQuery = useQuery({
    queryKey: ['designs'],
    // Load ALL designs (follow the cursor) so a design past the first 100 (name-sorted) is still
    // pickable when mapping a product. Not needed for caps or unproducible non-apparel (no
    // placements/designs to pick).
    enabled: !isCap && !isUnproducible,
    queryFn: () => fetchAllDesigns(client),
  });
  // A design is pickable once its ADULT file is READY; youth availability is shown per option.
  const readyDesigns = (designsQuery.data ?? []).filter((d) => d.adult.status === 'READY');

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      {/* Wide enough on a big screen to hold the mapping editor's two tracks (see EditorBody). Stepped
          rather than jumping straight to the widest: at `lg` a 64rem sheet on a 1024px viewport is a
          full-screen takeover, which a side sheet should never be. */}
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl xl:max-w-5xl 2xl:max-w-6xl">
        <SheetHeader>
          <div className="flex items-start gap-3">
            {product.featuredImageUrl ? (
              <button
                ref={thumbRef}
                type="button"
                onClick={() => setLightboxOpen(true)}
                title="View larger image"
                aria-label="View larger image"
                className="group relative shrink-0 cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <ProductThumb url={product.featuredImageUrl} size="lg" />
                <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100 motion-reduce:transition-none">
                  <ZoomIn className="size-5 text-white" />
                </span>
              </button>
            ) : (
              <ProductThumb url={product.featuredImageUrl} size="lg" />
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle>{product.title}</SheetTitle>
              <SheetDescription>
                {isCap
                  ? 'Map this cap to the S&S blank you order it as. It stays off the gang sheet.'
                  : isUnproducible
                    ? 'Not a produced garment — choose how FSP handles it below.'
                    : 'Map this product to an S&S blank + placements. Blanks resolve on save.'}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        {lightboxOpen && product.featuredImageUrl && (
          <ProductImageLightbox
            url={product.featuredImageUrl}
            title={product.title}
            onClose={() => setLightboxOpen(false)}
            triggerRef={thumbRef}
          />
        )}
        {detailQuery.isPending && <Skeleton className="m-4 h-72" />}
        {detailQuery.isError && (
          <Alert variant="destructive" className="m-4 w-auto">
            <AlertDescription>
              {detailQuery.error instanceof Error ? detailQuery.error.message : 'Load failed.'}
            </AlertDescription>
          </Alert>
        )}
        {detailQuery.isSuccess && (
          <ProductionClassControl
            storeId={storeId}
            product={product}
            effectiveClass={effectiveClass}
            override={detailQuery.data.classification?.productionClassOverride ?? null}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['catalog', storeId] });
              qc.invalidateQueries({ queryKey: ['catalog-caps', storeId] });
              qc.invalidateQueries({ queryKey: ['catalog-nonapparel', storeId] });
              qc.invalidateQueries({ queryKey: ['catalog-detail', storeId, product.productId] });
            }}
          />
        )}
        {detailQuery.isSuccess && isShipOnly && (
          <>
            <div className="mx-4 mb-3 rounded-lg border border-dashed px-3.5 py-3 text-sm text-muted-foreground">
              Ship-only: orders for this product appear on the{' '}
              <span className="font-medium text-foreground">Ship-only</span> worklist to be packed
              from shelf stock. No blank or design mapping is needed.
            </div>
            <ShipOnlyShelfMapper
              storeId={storeId}
              productId={product.productId}
              variants={detailQuery.data.variants}
              current={detailQuery.data.classification?.variantStockItems}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['catalog-detail', storeId, product.productId] });
                qc.invalidateQueries({ queryKey: ['fulfill'] });
                qc.invalidateQueries({ queryKey: ['stock-items'] });
              }}
            />
          </>
        )}
        {detailQuery.isSuccess && isUnproducible && (
          <div className="mx-4 mb-4 rounded-lg border border-dashed px-3.5 py-3 text-sm text-muted-foreground">
            FSP doesn’t produce this product, so there’s nothing to map. If FSP stocks and ships it,
            set <span className="font-medium text-foreground">Ship-only</span> above — its orders
            then appear on the Ship-only worklist to be packed. Otherwise leave it: its order lines
            stay out of production either way.
          </div>
        )}
        {detailQuery.isSuccess &&
          !isShipOnly &&
          !isUnproducible &&
          (isCap ? (
            <CapsEditorBody
              storeId={storeId}
              productId={product.productId}
              current={detailQuery.data.classification}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['catalog-caps', storeId] });
                qc.invalidateQueries({ queryKey: ['catalog-detail', storeId, product.productId] });
              }}
            />
          ) : (
            <EditorBody
              storeId={storeId}
              productId={product.productId}
              variants={detailQuery.data.variants}
              mapping={detailQuery.data.mapping}
              designs={readyDesigns}
              apparelKind={product.apparelKind}
              productImageUrl={product.featuredImageUrl}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['catalog', storeId] });
                qc.invalidateQueries({ queryKey: ['catalog-detail', storeId, product.productId] });
              }}
              onDeleted={() => {
                qc.invalidateQueries({ queryKey: ['catalog', storeId] });
                // Evict the stale detail (with the now-deleted mapping) so reopening this product
                // can't serve it from cache and pre-fill a create-form with the deleted placements.
                qc.removeQueries({ queryKey: ['catalog-detail', storeId, product.productId] });
                onClose();
              }}
            />
          ))}
      </SheetContent>
    </Sheet>
  );
}

/** The five choices for "Produced by FSP?" — '' means "no override, follow the automatic suggestion". */
const PRODUCTION_CLASS_OPTIONS: { value: '' | ProductionClassValue; label: string }[] = [
  { value: '', label: 'Default — follow the automatic suggestion' },
  { value: 'DTF_APPAREL', label: 'Produced — DTF apparel (gang sheet)' },
  { value: 'NON_DTF', label: 'Produced — cap / off-sheet' },
  { value: 'FULFILLMENT_ONLY', label: 'Ship-only — stocked and shipped, not produced' },
  { value: 'NOT_PRODUCED', label: 'Not handled by FSP' },
];
type ProductionClassValue = NonNullable<CatalogProductSummary['productionClass']>;

/**
 * "Produced by FSP?" — the per-product production-class override (inventory-clarity P2, closing the
 * caps-plan Phase-6 gap: `productionClassOverride` finally has a writer). An explicit override DECIDES
 * the lane server-side — including pulling an apparel-classified product off DTF (deliberate operator
 * intent is the one signal allowed past the silent-drop guard) — so the copy warns before reroutes.
 * Saves via the classification route's merge semantics: only `productionClassOverride` is sent, so a
 * cap's blank mapping is never clobbered.
 */
function ProductionClassControl({
  storeId,
  product,
  effectiveClass,
  override,
  onSaved,
}: {
  storeId: string;
  product: CatalogProductSummary;
  /** The override-folded class from the DETAIL (not the suggestion-derived list row). */
  effectiveClass: CatalogProductSummary['productionClass'];
  override: ProductionClassValue | null;
  onSaved: () => void;
}) {
  const client = useApiClient();
  const [value, setValue] = useState<'' | ProductionClassValue>(override ?? '');
  const selectId = useId();

  const save = useMutation({
    mutationFn: async (next: '' | ProductionClassValue) => {
      const { data, error } = await client.PUT(
        '/catalog/{storeId}/products/{productId}/classification',
        {
          params: { path: { storeId, productId: product.productId } },
          body: { productionClassOverride: next === '' ? null : next },
        },
      );
      if (error) throw new Error(error?.error?.detail ?? 'Failed to save the production class.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
    onSuccess: onSaved,
  });

  const dirty = value !== (override ?? '');
  // The scary direction: this product currently routes to DTF production and the operator is about to
  // reroute it. Open orders re-resolve on the next sync/reconcile (≤15 min), leaving the DTF queue.
  const pullingOffDtf = effectiveClass === 'DTF_APPAREL' && value !== '' && value !== 'DTF_APPAREL';

  return (
    <div className="mx-4 mb-3 space-y-2 rounded-lg border px-3.5 py-3">
      <Label htmlFor={selectId} className="text-sm font-medium">
        Produced by FSP?
      </Label>
      <p className="text-xs text-muted-foreground">
        Suggestion from the product’s Shopify category:{' '}
        <span className="font-medium text-foreground">
          {product.suggestedProductionClass ?? 'none'}
        </span>
        {override ? ' · overridden below' : ''}. Changes reroute open orders within ~15 minutes.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <select
          id={selectId}
          className="h-8 min-w-[20rem] rounded-md border bg-background px-2 text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value as '' | ProductionClassValue)}
        >
          {PRODUCTION_CLASS_OPTIONS.map((o) => (
            <option key={o.value || 'default'} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate(value)}>
          {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Save
        </Button>
      </div>
      {pullingOffDtf && dirty && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
          This pulls the product OUT of DTF production — its open lines leave the gang-sheet queue
          and move to the new lane. Only do this for items FSP doesn’t print.
        </p>
      )}
      {save.isError && (
        <p className="text-xs text-destructive">
          {save.error instanceof Error ? save.error.message : 'Save failed.'}
        </p>
      )}
    </div>
  );
}

/**
 * Shelf-stock mapping for a Ship-only product (P2b-B): each Shopify variant → one internal stock
 * item, so several listings (across stores) can draw from the same physical shelf count. Saves the
 * WHOLE map via the classification route (a provided map replaces the stored one; all-empty clears).
 * The fulfill worklist + item demand read this mapping live — no re-sync or reconcile needed.
 * Exported for unit tests.
 */
export function ShipOnlyShelfMapper({
  storeId,
  productId,
  variants,
  current,
  onSaved,
}: {
  storeId: string;
  productId: string;
  variants: CatalogVariantSummary[];
  current: Record<string, string> | undefined;
  onSaved: () => void;
}) {
  const client = useApiClient();
  const itemsQuery = useQuery({
    queryKey: ['stock-items'],
    queryFn: async () => {
      const { data, error } = await client.GET('/stock-items');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load stock items.');
      if (!data?.ok) throw new Error('Empty response.');
      return data.data.items;
    },
  });
  const pickable = (itemsQuery.data ?? []).filter((i) => !i.archived);

  const [map, setMap] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const v of variants) init[v.variantId] = current?.[v.variantId] ?? '';
    return init;
  });

  const save = useMutation({
    mutationFn: async () => {
      const entries = Object.entries(map).filter(([, slug]) => slug !== '');
      const { data, error } = await client.PUT(
        '/catalog/{storeId}/products/{productId}/classification',
        {
          params: { path: { storeId, productId } },
          body: { variantStockItems: entries.length ? Object.fromEntries(entries) : null },
        },
      );
      if (error || !data?.ok)
        throw new Error(error?.error?.detail ?? 'Failed to save the shelf mapping.');
      return data.data;
    },
    onSuccess: onSaved,
  });

  const dirty = variants.some((v) => (current?.[v.variantId] ?? '') !== (map[v.variantId] ?? ''));
  const variantLabel = (v: CatalogVariantSummary) =>
    v.variantTitle || [v.color, v.size].filter(Boolean).join(' / ') || 'Default';

  return (
    <div className="mx-4 mb-4 space-y-2 rounded-lg border px-3.5 py-3">
      <p className="text-sm font-medium">Shelf stock</p>
      <p className="text-xs text-muted-foreground">
        Which physical item each variant is packed from. Several listings can share one item — the
        Ship-only worklist and item demand follow this mapping immediately.
      </p>
      {variants.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          This product’s variants haven’t synced yet — run{' '}
          <span className="font-medium text-foreground">Sync from Shopify</span>, then reopen this
          product.
        </p>
      ) : itemsQuery.isPending ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Loading stock items…
        </p>
      ) : itemsQuery.isError ? (
        <p className="text-xs text-destructive">
          {itemsQuery.error instanceof Error ? itemsQuery.error.message : 'Failed to load items.'}
        </p>
      ) : pickable.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No stock items yet. Create them under{' '}
          <Link
            to="/inventory/items"
            className="font-medium text-foreground underline underline-offset-4"
          >
            Inventory → Stock items
          </Link>{' '}
          first, then map each variant here.
        </p>
      ) : (
        <>
          <div className="space-y-1.5">
            {variants.map((v) => (
              <div key={v.variantId} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{variantLabel(v)}</span>
                <select
                  aria-label={`Stock item for ${variantLabel(v)}`}
                  className="h-8 w-56 shrink-0 rounded-md border bg-background px-2 text-sm"
                  value={map[v.variantId] ?? ''}
                  onChange={(e) => setMap((m) => ({ ...m, [v.variantId]: e.target.value }))}
                >
                  <option value="">— no item mapped —</option>
                  {pickable.map((i) => (
                    <option key={i.slug} value={i.slug}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              Save shelf mapping
            </Button>
          </div>
          {save.isError && (
            <p className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed.'}
            </p>
          )}
          {save.isSuccess && !dirty && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">Shelf mapping saved.</p>
          )}
        </>
      )}
    </div>
  );
}

/** Exported for the component test — the per-colorway blank wiring is behavioural, not just lib logic. */
export function EditorBody({
  storeId,
  productId,
  variants,
  mapping,
  designs,
  apparelKind,
  productImageUrl,
  onSaved,
  onDeleted,
}: {
  storeId: string;
  productId: string;
  variants: CatalogVariantSummary[];
  mapping: import('./catalog-lib').ProductMappingView | undefined;
  designs: DesignSummary[];
  /** Ranks the derived style quick-pick: a hoodie product surfaces the hoodie pair first. */
  apparelKind: string | null | undefined;
  /** The product's featured image — the FALLBACK for a colourway with no photo of its own. */
  productImageUrl: string | null | undefined;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const client = useApiClient();
  const perColorBlankId = useId();
  const [blankStyle, setBlankStyle] = useState(mapping?.blankStyle ?? '');
  const [youthBlankStyle, setYouthBlankStyle] = useState(mapping?.youthBlankStyle ?? '');
  const [blankColor, setBlankColor] = useState(mapping?.blankColor ?? '');
  // `undefined` = the axis is OFF (the editor then sends null, so turning it off actually clears the
  // stored map). Seeded from what is stored, so reopening a per-colour product shows its map.
  const [blankColorByColor, setBlankColorByColor] = useState<Record<string, string> | undefined>(
    mapping?.blankColorByColor ? { ...mapping.blankColorByColor } : undefined,
  );
  // The last non-empty map, so toggling the axis off and on again is a NO-OP rather than a silent
  // rewrite to the product-level colour (P4 review, F2).
  const lastMapRef = useRef<Record<string, string> | undefined>(
    mapping?.blankColorByColor ? { ...mapping.blankColorByColor } : undefined,
  );
  // Whether the operator turned the axis OFF in this session. Only then does the save send an explicit
  // `null` to clear the stored map; otherwise the key is omitted so the server's KEEP applies. Without
  // this, a sheet opened before someone else enabled the axis would CLEAR their map on any later save —
  // and the fallback then resolves every colourway to one blank, RESOLVED and green (P4 review, F3).
  const turnedOffThisSession = useRef(false);
  const [drafts, setDrafts] = useState<PlacementDraft[]>(
    mapping && mapping.placements.length > 0
      ? mapping.placements.map(draftFromPlacement)
      : [emptyPlacementDraft()],
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [resolveResult, setResolveResult] = useState<{
    variantResolve: VariantResolveResult[];
    resolvedCount: number;
    totalVariants: number;
  } | null>(null);

  const youthPresent = hasYouthVariants(variants);
  const colorways = useMemo(() => distinctColorways(variants), [variants]);
  const perColorBlanks = blankColorByColor != null;
  const mappedColorways = colorways.filter(
    (c) => (blankColorByColor?.[c] ?? '').trim().length > 0,
  ).length;

  // The chosen styles' real colour lists, for the per-colorway membership check below. Same query key +
  // staleTime as ColorCombobox, so opening the picker costs nothing extra — this reuses its cache.
  // Written as two literal useQuery calls, not a helper that wraps one: a function that calls a hook is
  // only safe while every call site is unconditional, and nothing in the file would enforce that.
  const fetchStyleColors = async (style: string) => {
    const { data, error } = await client.GET('/garments/by-style/{style}', {
      params: { path: { style } },
    });
    if (error) throw new Error('Could not load this style’s colours.');
    return data.data;
  };
  const adultColors = useQuery({
    queryKey: ['style-colors', blankStyle.trim()],
    enabled: blankStyle.trim().length > 0,
    staleTime: 5 * 60_000,
    queryFn: () => fetchStyleColors(blankStyle.trim()),
  });
  const youthColors = useQuery({
    queryKey: ['style-colors', youthBlankStyle.trim()],
    enabled: youthBlankStyle.trim().length > 0,
    staleTime: 5 * 60_000,
    queryFn: () => fetchStyleColors(youthBlankStyle.trim()),
  });
  // ⚠️ `undefined` means UNKNOWN (not loaded / errored) and MUST NOT render as "this colour is fine".
  // Only a successfully loaded, non-empty list becomes a set the row is allowed to warn from.
  const nameSet = (names: string[] | undefined) =>
    names && names.length > 0 ? new Set(names) : undefined;
  const adultColorNames = nameSet(adultColors.data?.colors.map((c) => c.colorName));
  const youthColorNames = nameSet(youthColors.data?.colors.map((c) => c.colorName));

  // The shop's own (adult, youth) style pairs, ranked for THIS product's apparel kind. Fallback-safe
  // server-side, so a failure here just means no quick-pick — never a blocked save.
  const pairsQuery = useQuery({
    queryKey: ['blank-style-pairs'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/catalog/blank-style-pairs', {});
      if (error) throw new Error('Could not load the shop’s blank styles.');
      return data.data;
    },
  });
  const rankedPairs = useMemo(
    () => rankStylePairs(pairsQuery.data?.pairs ?? [], apparelKind),
    [pairsQuery.data, apparelKind],
  );

  const togglePerColorBlanks = (on: boolean) => {
    if (!on) {
      // Remember what was there. Toggling off used to DISCARD the map outright, and toggling back on
      // reseeded every colourway from the product-level colour — so one misclick round-trip on a
      // correctly retrofitted product silently rewrote five declared colours to "Black", showed a
      // healthy "5/5 colorways given a blank colour", and re-stamped every variant onto the one black
      // sku: back into the exact state this axis exists to remove. (P4 review, F2.)
      lastMapRef.current = blankColorByColor ?? lastMapRef.current;
      setBlankColorByColor(undefined);
      turnedOffThisSession.current = true;
      return;
    }
    turnedOffThisSession.current = false;
    // Restore the previous map when there is one; only seed from the product-level colour for a product
    // that has never had one. The seed's rationale — "turning it on changes nothing yet" — is only true
    // when nothing is stored.
    setBlankColorByColor(
      lastMapRef.current ?? Object.fromEntries(colorways.map((c) => [c, blankColor.trim()])),
    );
  };

  const save = useMutation({
    mutationFn: async () => {
      const built = buildMappingSaveRequest(
        blankStyle,
        youthBlankStyle,
        blankColor,
        drafts,
        colorways,
        // A map turns the axis on. `null` turns it off — but ONLY when the operator turned it off here,
        // because `null` CLEARS and this editor is not the only writer. When the axis was simply never
        // on in this sheet, the key is omitted so the server KEEPS whatever is stored, which is what
        // stops a stale sheet from clearing a map somebody else just saved.
        blankColorByColor ?? (turnedOffThisSession.current ? null : undefined),
      );
      if (!built.ok) throw new Error(built.error);
      const res = await client.PUT('/catalog/{storeId}/products/{productId}/mapping', {
        params: { path: { storeId, productId } },
        body: built.request,
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Save failed.');
      if (!res.data?.ok) throw new Error('Empty response.');
      return res.data.data;
    },
    onSuccess: (data) => {
      setFormError(null);
      setResolveResult({
        variantResolve: data.variantResolve,
        resolvedCount: data.resolvedCount,
        totalVariants: data.totalVariants,
      });
      onSaved();
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Save failed.'),
  });

  const del = useMutation({
    mutationFn: async () => {
      const res = await client.DELETE('/catalog/{storeId}/products/{productId}/mapping', {
        params: { path: { storeId, productId } },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Delete failed.');
      if (!res.data?.ok) throw new Error('Empty response.');
      return res.data.data;
    },
    onSuccess: onDeleted,
    onError: (err) => setFormError(err instanceof Error ? err.message : 'Delete failed.'),
  });

  const updateDraft = (i: number, patch: Partial<PlacementDraft>) =>
    setDrafts((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDraft = (i: number) => setDrafts((ds) => ds.filter((_, idx) => idx !== i));
  const addDraft = () => setDrafts((ds) => [...ds, emptyPlacementDraft()]);

  const resolveByVariant = useMemo(
    () => new Map((resolveResult?.variantResolve ?? []).map((r) => [r.variantId, r])),
    [resolveResult],
  );

  const designNameById = useMemo(
    () => new Map(designs.map((d) => [d.designId, d.name])),
    [designs],
  );
  const colorwayPlans = useMemo(
    () =>
      buildColorwayPlans({
        variants,
        drafts,
        designNameById,
        productImageUrl,
        liveByVariant: resolveByVariant,
      }),
    [variants, drafts, designNameById, productImageUrl, resolveByVariant],
  );

  // Computed from the LIVE result when there is one, else the stored rows — so the explainer appears on
  // the save that creates the situation, not only after a reload.
  const sharedBlankGroups = useMemo(
    () =>
      colorwaysSharingOneBlank(
        variants.map((v) => ({
          color: v.color,
          resolvedBlankSku: resolveByVariant.get(v.variantId)
            ? resolveByVariant.get(v.variantId)?.resolvedBlankSku
            : v.resolvedBlankSku,
        })),
      ),
    [variants, resolveByVariant],
  );

  return (
    <div className="@container flex flex-1 flex-col gap-5 overflow-y-auto p-4">
      {/* N colourways → 1 blank sku. NOT an alarm: for a product whose Shopify option is an ART
          colourway on one black blank this is exactly right, and that is most of the catalogue — an
          alert there would train the operator to dismiss it. So the severity comes from what the
          operator DECLARED with the toggle, and the copy names BOTH readings. */}
      {!perColorBlanks && sharedBlankGroups.length > 0 && (
        <Alert>
          <AlertTitle>
            {sharedBlankGroups[0]?.colorways.length} colorways print on the same blank
          </AlertTitle>
          <AlertDescription>
            {/* ⚠️ ONE <p>, not loose inline children. `AlertDescription` is a `grid gap-1`, so every
                child — including each text node either side of a <span> — becomes its own grid ROW.
                Multiple children render as a ransom note, and no jsdom assertion can see it because
                getByText still finds every fragment. */}
            <p>
              {sharedBlankGroups[0]?.colorways.join(', ')} all draw{' '}
              <span className="font-mono">{bareBlankSku(sharedBlankGroups[0]?.sku ?? '')}</span>.
              That's correct if the Shopify colour names an artwork colourway printed on one blank.
              If they are real garment colours, turn on{' '}
              <span className="font-medium">different blank colour per colour</span> below —
              otherwise every colour is ordered and pressed as this one.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* TWO TRACKS — the recipe left, its consequences right, per the v2 mockup (docs/mockups/
          mapping-modal-v2). Keyed to the CONTAINER, not the viewport: the split may only engage once
          the SHEET is actually wide enough to hold two tracks, and the sheet's own max-width is what
          decides that. A viewport breakpoint could disagree with the sheet and starve both columns.
          ⚠️ The shared-blank warning above deliberately stays OUTSIDE this grid. The mockup files it
          in the right column, which is fine side-by-side but puts it BELOW the placements once the
          grid collapses to one column — re-burying the one warning the review section exists to
          surface. Full-width and first is right at every width. */}
      <div className="grid grid-cols-1 gap-5 @3xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] @3xl:gap-6">
        {/* LEFT — what the operator decides */}
        <div className="flex min-w-0 flex-col gap-5">
          {/* Blank styles */}
          <section className="space-y-3">
            {/* Both columns carry a heading or neither reads as a column — with the split live, the
                left previously opened on a bare "Used most" label while the right had a title. */}
            <h3 className="text-sm font-medium">Garment</h3>
            {/* Quick-pick, DERIVED from the shop's own mapping history — not a list in the code. Two style
            pairs cover every mapping the shop has ever made, so this replaces two combobox searches
            with one click; a new garment appears here the first time anyone maps to it. */}
            {rankedPairs.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-normal text-muted-foreground">
                  Used most{' '}
                  {pairsQuery.data ? `(of ${pairsQuery.data.totalMappings} mapped products)` : ''}
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {rankedPairs.slice(0, 4).map((p) => {
                    const active = p.blankStyle === blankStyle.trim();
                    return (
                      <Button
                        key={`${p.blankStyle}|${p.youthBlankStyle ?? ''}`}
                        type="button"
                        variant={active ? 'secondary' : 'outline'}
                        size="sm"
                        aria-pressed={active}
                        className="h-auto flex-col items-start gap-0 px-2.5 py-1.5 text-left"
                        onClick={() => {
                          setBlankStyle(p.blankStyle);
                          setYouthBlankStyle(p.youthBlankStyle ?? '');
                        }}
                      >
                        <span className="text-xs font-medium">
                          {p.blankStyle}
                          {p.youthBlankStyle ? ` / ${p.youthBlankStyle}` : ''}
                        </span>
                        <span className="text-[11px] font-normal text-muted-foreground">
                          {p.apparelKinds.join(', ') || 'no kind'} · {p.productCount}{' '}
                          {p.productCount === 1 ? 'product' : 'products'}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Adult blank style</Label>
              <StyleCombobox value={blankStyle} onPick={(pick) => setBlankStyle(pick.style)} />
            </div>
            <div className="space-y-1.5">
              <Label>
                Youth blank style{' '}
                <span className="font-normal text-muted-foreground">
                  {youthPresent ? '(this product has youth sizes)' : '(optional)'}
                </span>
              </Label>
              <StyleCombobox
                value={youthBlankStyle}
                onPick={(pick) => setYouthBlankStyle(pick.style)}
              />
              {youthPresent && youthBlankStyle.trim().length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Youth variants stay unresolved until a youth blank style is set.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>
                Blank color{' '}
                <span className="font-normal text-muted-foreground">
                  {perColorBlanks
                    ? '(not used — each colour has its own below)'
                    : '(applies to both bands)'}
                </span>
              </Label>
              {/* Picked from the style's REAL S&S colours, never typed. The colour cascade is 7 tiers deep
              and its later tiers match on family, so a hand-typed near-miss resolves to a real-but-WRONG
              shade with no warning — a house "Red" resolves to "Antique Cherry Red". Only tier 1 is
              exact equality, so naming the shade by selection is the only safe input. */}
              <ColorCombobox
                style={blankStyle}
                value={blankColor}
                onPick={(c) => setBlankColor(c)}
              />
              <p className="text-xs text-muted-foreground">
                {perColorBlanks ? (
                  <>
                    Ignored while each colour has its own blank. A colorway with no blank colour
                    below is <span className="font-medium text-foreground">blocked</span> — never
                    given this colour.
                  </>
                ) : (
                  <>
                    The S&amp;S blank color to order, for every colorway of this product. Leave
                    empty only if the Shopify variants already carry the exact S&amp;S colour name.
                  </>
                )}
              </p>
            </div>

            {/* Per-colorway BLANK colour — the second per-colorway axis, for a product whose Shopify option
            is a real GARMENT colour rather than an art colourway. */}
            {colorways.length >= 2 && (
              <div className="space-y-2">
                <label
                  htmlFor={perColorBlankId}
                  className="flex items-center gap-2 text-xs text-muted-foreground"
                >
                  <Checkbox
                    id={perColorBlankId}
                    checked={perColorBlanks}
                    onCheckedChange={(v) => togglePerColorBlanks(v === true)}
                  />
                  Different blank colour per colour ({colorways.length} colorways)
                </label>
                {perColorBlanks && blankColorByColor && (
                  <div className="space-y-1.5 rounded-md border border-dashed p-2">
                    {colorways.map((color) => {
                      const picked = blankColorByColor[color] ?? '';
                      const missingAdult =
                        adultColorNames && picked && !adultColorNames.has(picked);
                      const missingYouth =
                        youthPresent && youthColorNames && picked && !youthColorNames.has(picked);
                      return (
                        <div key={color} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="w-24 shrink-0 truncate text-xs font-medium"
                              title={color}
                            >
                              {color}
                            </span>
                            <div className="min-w-0 flex-1">
                              <ColorCombobox
                                style={blankStyle}
                                value={picked}
                                ariaLabel={`Blank colour for ${color}`}
                                onPick={(c) =>
                                  setBlankColorByColor((m) => ({ ...(m ?? {}), [color]: c }))
                                }
                              />
                            </div>
                          </div>
                          {/* Only ever rendered from a LOADED colour list — an unknown says nothing rather
                          than implying the colour is fine. The server refuses correctly either way;
                          this just moves the discovery before the save. */}
                          {(missingAdult || missingYouth) && (
                            <p className="pl-26 text-xs text-amber-600 dark:text-amber-400">
                              {missingAdult
                                ? `${blankStyle || 'The adult style'} has no colour named “${picked}” — these variants will stay unresolved.`
                                : `${youthBlankStyle} has no colour named “${picked}” — this colorway's YOUTH variants will stay unresolved.`}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    <p className="text-xs text-muted-foreground">
                      {mappedColorways}/{colorways.length} colorways given a blank colour
                      {mappedColorways < colorways.length &&
                        ' — you can save partially; a colorway with none is blocked until you add it, never given the product-level colour.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Placements */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Placements</h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addDraft}
                disabled={drafts.length >= MAX_PLACEMENTS}
              >
                <Plus className="size-4" /> Add
              </Button>
            </div>
            <div className="space-y-3">
              {drafts.map((d, i) => (
                <PlacementRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional; no stable id exists pre-save.
                  key={i}
                  draft={d}
                  designs={designs}
                  colorways={colorways}
                  canRemove={drafts.length > 1}
                  onChange={(patch) => updateDraft(i, patch)}
                  onRemove={() => removeDraft(i)}
                />
              ))}
            </div>
          </section>
        </div>

        {/* RIGHT — what those decisions will actually pull. Read-only; nothing here is an input. */}
        <div className="flex min-w-0 flex-col gap-4 @3xl:border-l @3xl:pl-6">
          {/* What each colourway will pull — the mapping REVIEW (P3b).
          This replaced a 50-row size table at the TOP of the sheet. Two problems with that table: it
          answered the wrong question (decisions are per COLOURWAY; sizes only multiply rows), and on a
          real product — Low Trendz Logo Tee, 50 variants — it pushed every control about three screens
          below the fold, including the "5 colourways print on the same blank" warning. */}
          {colorwayPlans.length > 0 && (
            <section className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium">What each colourway will pull</h3>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {colorwayPlans.length} {colorwayPlans.length === 1 ? 'colourway' : 'colourways'}
                </span>
              </div>
              <div className="divide-y rounded-lg border">
                {colorwayPlans.map((plan) => (
                  <ColorwayPlanRow
                    key={plan.colorway}
                    plan={plan}
                    productImageUrl={productImageUrl}
                    perColorBlanks={perColorBlanks}
                  />
                ))}
              </div>
            </section>
          )}

          {/* The full size breakdown, now on demand. Kept verbatim — it is still the only place to see a
          single size's own state, which matters when one size of a colourway fails to resolve. */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
              Show all {variants.length} sizes
            </summary>
            <section className="mt-2 space-y-2">
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full min-w-[420px] text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        Colour / size
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        Band
                      </th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        Blank
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v) => {
                      // Prefer the just-saved resolve result; else the stored status from the detail load.
                      // When a live result exists it is AUTHORITATIVE for BOTH fields — never fall back to
                      // the stored sku, or an un-resolved re-save would briefly show the old blank sku.
                      const live = resolveByVariant.get(v.variantId);
                      const status = live ? live.blankResolveStatus : v.blankResolveStatus;
                      const sku = live ? live.resolvedBlankSku : v.resolvedBlankSku;
                      // The live result now carries the matched blank's IDENTITY too (P3), so a just-saved
                      // row reads `Tultex 602 · Heather Grey · L` and not a bare sku. That matters because the
                      // sku is the one field no operator can check by eye — `Saved — 5/5 resolved` was a full
                      // green for a mapping where every colourway had landed on the same black blank.
                      const identity = live ?? v;
                      const blankLabel = blankIdentityLabel({
                        brandName: identity.blankBrandName,
                        styleName: identity.blankStyleName,
                        colorName: identity.blankColorName,
                        sizeName: identity.blankSizeName,
                      });
                      // WHY it didn't resolve. The live reason wins; else the one stamped on the row, which is
                      // what makes a refusal discovered by the catalog-sync worker legible at all.
                      const reason = live ? live.reason : v.blankResolveReason;
                      const meta = CATALOG_BLANK_RESOLVE_META[status];
                      return (
                        <tr key={v.variantId} className="border-t [&:nth-child(even)]:bg-muted/20">
                          <td className="px-2 py-1.5">
                            {[v.color, v.size].filter(Boolean).join(' / ') ||
                              v.variantTitle ||
                              v.variantId}
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground">{v.sizeBand}</td>
                          <td className="px-2 py-1.5">
                            {sku ? (
                              blankLabel ? (
                                <span>
                                  {blankLabel}{' '}
                                  <span className="ml-1 font-mono text-xs text-muted-foreground">
                                    {bareBlankSku(sku)}
                                  </span>
                                </span>
                              ) : (
                                <span className="font-mono text-xs">{bareBlankSku(sku)}</span>
                              )
                            ) : (
                              <div className="space-y-0.5">
                                <Badge variant="outline" className={meta.className}>
                                  {meta.label}
                                </Badge>
                                {reason && (
                                  <p className="max-w-[22rem] text-xs text-muted-foreground">
                                    {reason}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </details>
        </div>
      </div>

      {formError && (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      {resolveResult && (
        <Alert
          variant={
            resolveResult.resolvedCount === resolveResult.totalVariants ? undefined : 'destructive'
          }
        >
          <AlertTitle>
            Saved — {resolveResult.resolvedCount}/{resolveResult.totalVariants} variants resolved
          </AlertTitle>
          {resolveResult.resolvedCount < resolveResult.totalVariants && (
            <AlertDescription>
              {resolveResult.variantResolve
                .filter((r) => r.blankResolveStatus !== 'RESOLVED')
                .slice(0, 3)
                .map((r) => r.reason)
                .filter(Boolean)
                .join(' · ') || 'Some variants did not resolve.'}
            </AlertDescription>
          )}
        </Alert>
      )}

      <SheetFooter className="mt-auto flex-row justify-between gap-2 px-0">
        {mapping ? (
          <Button
            type="button"
            variant="outline"
            className="text-destructive"
            disabled={del.isPending || save.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Remove mapping
          </Button>
        ) : (
          <span />
        )}
        <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending && <Loader2 className="size-4 animate-spin" />}
          {mapping ? 'Update mapping' : 'Save mapping'}
        </Button>
      </SheetFooter>
    </div>
  );
}

/**
 * One colourway's whole mapping decision, on one row (P3b).
 *
 * The garment photo is the point: a per-colourway decision cannot be confirmed against the product's
 * featured image, because that image is ONE colour. On Low Trendz Logo Tee the featured photo is the RED
 * tee while the operator is also deciding what Grey and White pull — so a fallback photo is LABELLED as
 * the product's, never presented as this colourway's garment. Click to zoom: the design is often a small
 * chest logo, and "is that logo black or white?" is exactly the question that decides the mapping.
 */
function ColorwayPlanRow({
  plan,
  productImageUrl,
  perColorBlanks,
}: {
  plan: ColorwayPlan;
  productImageUrl: string | null | undefined;
  perColorBlanks: boolean;
}) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const imgRef = useRef<HTMLButtonElement>(null);
  const isFallback = usesFallbackImage(plan, productImageUrl);
  const blocked = plan.designs.filter((d) => d.designId === null);
  // Sharing a blank is only worth flagging when the operator has DECLARED per-colour blanks: without
  // that declaration it is the correct, intended state for every art-colourway product.
  const collides = perColorBlanks && plan.sharesBlankWith.length > 0;

  return (
    <div className="flex items-start gap-3 p-2.5">
      {plan.imageUrl ? (
        <button
          ref={imgRef}
          type="button"
          onClick={() => setZoomOpen(true)}
          title={`View ${plan.colorway} larger`}
          aria-label={`View ${plan.colorway} larger`}
          className="group relative shrink-0 cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src={withImageWidth(plan.imageUrl, 112)}
            alt=""
            loading="lazy"
            className="size-14 rounded-md border object-cover"
          />
          <span className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100 motion-reduce:transition-none">
            <ZoomIn className="size-4 text-white" />
          </span>
        </button>
      ) : (
        <div className="flex size-14 shrink-0 items-center justify-center rounded-md border bg-muted/40">
          <Boxes className="size-5 text-muted-foreground" />
        </div>
      )}
      {zoomOpen && plan.imageUrl && (
        <ProductImageLightbox
          url={plan.imageUrl}
          title={`${plan.colorway}${isFallback ? ' — product photo' : ''}`}
          onClose={() => setZoomOpen(false)}
          triggerRef={imgRef}
        />
      )}

      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium">{plan.colorway}</span>
          <span className="text-xs text-muted-foreground">
            {plan.sizeCount} {plan.sizeCount === 1 ? 'size' : 'sizes'}
          </span>
          {isFallback && (
            <span className="text-xs text-muted-foreground">· product photo, not this colour</span>
          )}
        </div>

        {/* The blank. A colour NAME, because the SKU is the one thing nobody can check by eye. */}
        <p className="text-xs">
          {plan.unresolvedCount === plan.sizeCount ? (
            <span className="text-amber-600 dark:text-amber-400">
              No blank — {plan.unresolvedReason ?? 'these sizes did not resolve.'}
            </span>
          ) : (
            <>
              <span className="text-muted-foreground">Blank </span>
              {plan.blankLabel ? (
                <>
                  <span className="font-medium">{plan.blankLabel}</span>
                  {/* The SKUs differ only by SIZE, so listing all ten is noise — the colour name is the
                      decision. Measured on Low Trendz Logo Tee: ten SKUs per row swamped the row and
                      buried the design line under them. */}
                  {plan.blankSkus.length > 1 && (
                    <span className="ml-1.5 text-[11px] text-muted-foreground">
                      · {plan.blankSkus.length} SKUs, one per size
                    </span>
                  )}
                  {plan.blankSkus.length === 1 && (
                    <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">
                      {plan.blankSkus[0]}
                    </span>
                  )}
                </>
              ) : plan.blankColorNames.length > 1 ? (
                /* TWO OR MORE colours across this colourway's OWN sizes — a real inconsistency, not
                   staleness, and a re-save would only re-stamp the same split. Naming both is the whole
                   point of this row: it exists to make the blank visible. */
                <span className="text-amber-600 dark:text-amber-400">
                  <span className="font-medium">{plan.blankColorNames.join(' + ')}</span> — this
                  colour's sizes resolved to different blank colours
                </span>
              ) : (
                <>
                  {/* NO colour name at all. Real state, not a bug: variants resolved before the
                      denormalized identity existed carry the SKU only, and a re-save stamps them. Say
                      that, rather than printing the word "resolved" and looking like it means something. */}
                  <span className="font-mono font-medium">{plan.blankSkus[0]}</span>
                  <span className="ml-1.5 text-[11px] text-muted-foreground">
                    colour not recorded
                    {plan.blankSkus.length > 1 ? ` · ${plan.blankSkus.length} sizes` : ''} — re-save
                    to stamp it
                  </span>
                </>
              )}
              {plan.unresolvedCount > 0 && (
                <span className="ml-1.5 text-amber-600 dark:text-amber-400">
                  · {plan.unresolvedCount} size{plan.unresolvedCount === 1 ? '' : 's'} unresolved
                </span>
              )}
            </>
          )}
        </p>

        {/* The design per placement — `blocked` mirrors the server's refusal, never the default art. */}
        {plan.designs.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {plan.designs.map((d, i) => (
              <span key={d.location}>
                {i > 0 && ' · '}
                {d.location}{' '}
                {d.designId === null ? (
                  <span className="font-medium text-amber-600 dark:text-amber-400">no design</span>
                ) : (
                  <span className="font-medium text-foreground">{d.designName ?? d.designId}</span>
                )}
              </span>
            ))}
          </p>
        )}

        {collides && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Same blank as {plan.sharesBlankWith.join(', ')} — deliberate only if they really are the
            same garment colour.
          </p>
        )}
        {blocked.length > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Blocks at build until {blocked.map((d) => d.location).join(' + ')} has a design for this
            colour.
          </p>
        )}
      </div>
    </div>
  );
}

function PlacementRow({
  draft,
  designs,
  colorways,
  canRemove,
  onChange,
  onRemove,
}: {
  draft: PlacementDraft;
  designs: DesignSummary[];
  colorways: string[];
  canRemove: boolean;
  onChange: (patch: Partial<PlacementDraft>) => void;
  onRemove: () => void;
}) {
  const perColorId = useId();
  const perColor = draft.designByColor != null;
  // Only offer per-colorway when the product actually has ≥2 distinct colorways to differentiate.
  const canPerColor = colorways.length >= 2;

  const togglePerColor = (on: boolean) => {
    if (on) {
      // Seed EVERY colorway with the current default so the map is complete by construction —
      // no colorway silently drops to BLOCK at batch time (the operator then edits each one).
      onChange({
        designByColor: Object.fromEntries(colorways.map((c) => [c, draft.designId])),
      });
    } else {
      onChange({ designByColor: undefined });
    }
  };

  return (
    <div className="space-y-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Select value={draft.location} onValueChange={(v) => onChange({ location: v })}>
          <SelectTrigger className="flex-1" aria-label="Location">
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            {PLACEMENT_LOCATION_OPTIONS.map((loc) => (
              <SelectItem key={loc} value={loc}>
                {loc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={draftMethod(draft)}
          onValueChange={(v) =>
            // Store `undefined` for DTF rather than the literal — the field is sparse by contract, so
            // an untouched DTF row keeps saving exactly as it does today.
            onChange({ method: v === 'screen-print' ? 'screen-print' : undefined })
          }
        >
          <SelectTrigger className="w-44" aria-label="Print method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="dtf">DTF · gang sheet</SelectItem>
            <SelectItem value="screen-print">Screen print</SelectItem>
          </SelectContent>
        </Select>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove placement"
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>

      {draftMethod(draft) === 'screen-print' && (
        // Says what the setting DOES, not what it is. The operator's real question is "will this still
        // print film?", and the answer — never, by any route — is the whole reason the field exists.
        <p className="rounded-md border border-violet-300 bg-violet-50 px-2 py-1.5 text-xs text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300">
          Produced off the gang sheet. No build can put this placement on DTF film — not by the
          per-sheet screen-print tick-boxes either. Its art still needs a design here so the run
          sheet and hem tags can name it. Sheets built BEFORE this change keep the technique they
          were built with, so reprinting one still reproduces what was physically made.
        </p>
      )}

      {/* Default design — required, and the fallback when per-colorway is off. */}
      <DesignCombobox
        value={draft.designId}
        designs={designs}
        ariaLabel={perColor ? 'Default design' : 'Design'}
        onChange={(v) => onChange({ designId: v })}
      />

      {canPerColor && (
        <label
          htmlFor={perColorId}
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <Checkbox
            id={perColorId}
            checked={perColor}
            onCheckedChange={(v) => togglePerColor(v === true)}
          />
          Different design per color ({colorways.length} colorways)
        </label>
      )}

      {perColor && draft.designByColor && (
        <div className="space-y-1.5 rounded-md border border-dashed p-2">
          {colorways.map((color) => (
            <div key={color} className="flex items-center gap-2">
              <span className="w-24 shrink-0 truncate text-xs font-medium" title={color}>
                {color}
              </span>
              <DesignCombobox
                value={draft.designByColor?.[color] ?? ''}
                designs={designs}
                ariaLabel={`Design for ${color}`}
                onChange={(v) =>
                  onChange({ designByColor: { ...draft.designByColor, [color]: v } })
                }
              />
            </div>
          ))}
          {(() => {
            const mapped = colorways.filter(
              (c) => (draft.designByColor?.[c] ?? '').trim().length > 0,
            ).length;
            return (
              <p className="text-xs text-muted-foreground">
                {mapped}/{colorways.length} colorways mapped
                {mapped < colorways.length &&
                  ' — you can save partially; unmapped colorways stay blocked until you add their design.'}
              </p>
            );
          })()}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Print size comes from the design file (1 in = 300 px). Youth prints from the design's
        separate youth file — a youth order for a design without one stays blocked.
      </p>
    </div>
  );
}

/** " · 12″×16″ · +youth" style hint from the design's native (adult) print size + youth availability. */
